import {
  DatePickerField,
  FormProvider,
  ReadOnlyField,
  SelectField,
  SimpleGrid,
  Stack,
  useForm,
} from '@agrotoken-tech/ui';
import { formatDate } from '@bcba/common-utils';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import { CommonCropPledgeDebtorInformationSerializer } from '@bcba/serializers';
import {
  CROP_PLEDGE_USER_ROLES,
  MARITAL_STATUSES,
  NATIONALITIES,
  PLEDGE_SECTION_STATUSES,
} from '@bcba/types';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
} from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  patchApproveCropPledgeDebtorInformation,
  patchCropPledgeDebtorInformation,
} from '../../../../services/crop-pledge';
import { CommonUpdateCropPledgeDebtorInformationDto } from '../../../../services/crop-pledge/dto/update';
import { CropPledgeSectionActions } from '../../shared/CropPledgeSectionActions';
import { useCropPledgeContext } from '../context/CropPledgeContext';

interface Props {
  data: CommonCropPledgeDebtorInformationSerializer;
  refetch: (
    options?: RefetchOptions,
  ) => Promise<
    QueryObserverResult<CommonCropPledgeDebtorInformationSerializer, Error>
  >;
}

const resolver = classValidatorResolver(
  CommonUpdateCropPledgeDebtorInformationDto,
);
export const PledgeDebtorPFForm = ({
  data,
  refetch: refetchDebtorData,
}: Props) => {
  const {
    fiscalId,
    representantFirstName,
    representantLastName,
    representantNationality,
    representantBirthDate,
    representantMaritalStatus,
    representantIdentityDocumentType,
    representantIdentityDocumentId,
    streetName,
    streetNumber,
    province,
    locality,
    postalCode,
    email,
    phoneNumber,
  } = data;
  const { t } = useTranslation();
  const { uuid = '' } = useParams();
  const { showSuccessToast } = useToastsHook();

  const {
    role,
    cropPledgeStatus,
    refetchCropPledgeStatus,
    isPledgeInAnEditableStage,
  } = useCropPledgeContext();

  const { sections } = cropPledgeStatus ?? {};
  const { debtor: debtorSection } = sections ?? {};

  const nationalitiesOptions = Object.values(NATIONALITIES).map((value) => ({
    label: t(`nationality.${value}`),
    value,
  }));

  const maritalStatusOptions = Object.values(MARITAL_STATUSES).map((value) => ({
    label: t(`common.maritalStatus.${value}`),
    value,
  }));

  const mapDataToDto = (
    data: CommonCropPledgeDebtorInformationSerializer,
  ): CommonUpdateCropPledgeDebtorInformationDto => ({
    isPFCompanyType: true,
    representantBirthDate: data.representantBirthDate ?? undefined,
    representantMaritalStatus: data.representantMaritalStatus ?? undefined,
    representantNationality: data.representantNationality ?? undefined,
  });

  const formMethods = useForm<CommonUpdateCropPledgeDebtorInformationDto>({
    resolver,
    defaultValues: mapDataToDto(data),
  });
  const {
    handleSubmit,
    formState: { isDirty },
  } = formMethods;

  const { mutateAsync: reqApproval, isPending: isPendingRequestApproval } =
    useMutation({
      mutationFn: (payload?: CommonUpdateCropPledgeDebtorInformationDto) =>
        patchCropPledgeDebtorInformation(uuid, payload).then(() => {
          refetchDebtorData();
          refetchCropPledgeStatus();
        }),
      onSuccess: () => {
        showSuccessToast({ title: t('common.requestApproval.toast.success') });
      },
    });

  const { mutateAsync: approveSection, isPending: isPendingSectionApproval } =
    useMutation({
      mutationFn: () =>
        patchApproveCropPledgeDebtorInformation(uuid).then(() => {
          refetchDebtorData();
          refetchCropPledgeStatus();
        }),
      onSuccess: () =>
        showSuccessToast({ title: t('common.approve.toast.success') }),
    });

  const onSubmit = (data: CommonUpdateCropPledgeDebtorInformationDto) => {
    if (role === CROP_PLEDGE_USER_ROLES.DEBTOR) {
      reqApproval(data);
    } else if (role === CROP_PLEDGE_USER_ROLES.CREDITOR) {
      approveSection();
    }
  };

  const isFormReadOnly =
    !isPledgeInAnEditableStage ||
    role === CROP_PLEDGE_USER_ROLES.CREDITOR ||
    (role === CROP_PLEDGE_USER_ROLES.DEBTOR &&
      debtorSection?.status !== PLEDGE_SECTION_STATUSES.PENDING_DEBTOR_LOAD &&
      debtorSection?.status !== PLEDGE_SECTION_STATUSES.PENDING_BOLSA_APPROVAL);

  return (
    <Stack mt={6} gap={6}>
      <SimpleGrid columns={2} spacing={6}>
        <ReadOnlyField label={t('common.CUIT/CUIL')} value={fiscalId} />
        <ReadOnlyField
          label={t('common.name')}
          value={representantFirstName || ''}
        />
        <ReadOnlyField
          label={t('common.lastName')}
          value={representantLastName || ''}
        />
        <ReadOnlyField
          label={t('common.documentType')}
          value={representantIdentityDocumentType || ''}
        />
        <ReadOnlyField
          label={t('common.document')}
          value={representantIdentityDocumentId || ''}
        />
        {!isFormReadOnly ? (
          <FormProvider {...formMethods}>
            <SelectField
              options={nationalitiesOptions}
              label={t('common.nationality.label')}
              placeholder={t('common.nationality.placeholder')}
              name="representantNationality"
              rhfOptions={{ required: true }}
              errorMessageFormatter={t}
            />
            <DatePickerField
              name="representantBirthDate"
              label={t('common.birthDate.label')}
              placeholder={t('common.birthDate.placeholder')}
              rhfOptions={{ required: true }}
              errorMessageFormatter={t}
            />
            <SelectField
              options={maritalStatusOptions}
              label={t('common.maritalStatus.label')}
              placeholder={t('common.maritalStatus.placeholder')}
              name="representantMaritalStatus"
              rhfOptions={{ required: true }}
              errorMessageFormatter={t}
            />
          </FormProvider>
        ) : (
          <>
            <ReadOnlyField
              label={t('common.nationality.label')}
              value={
                representantNationality
                  ? t(`nationality.${representantNationality}`)
                  : ''
              }
            />
            <ReadOnlyField
              label={t('common.birthDate.label')}
              value={
                representantBirthDate ? formatDate(representantBirthDate) : ''
              }
            />
            <ReadOnlyField
              label={t('common.maritalStatus.label')}
              value={
                representantMaritalStatus
                  ? t(`common.maritalStatus.${representantMaritalStatus}`)
                  : ''
              }
            />
          </>
        )}
        <ReadOnlyField
          label={t('businessSignUp.form.addressStreet.label')}
          value={streetName || ''}
        />
        <ReadOnlyField
          label={t('businessSignUp.form.addressNumber.label')}
          value={streetNumber || ''}
        />
        <ReadOnlyField
          label={t('businessSignUp.form.state.label')}
          value={province || ''}
        />
        <ReadOnlyField
          label={t('businessSignUp.form.locality.label')}
          value={locality || ''}
        />
        <ReadOnlyField
          label={t('businessSignUp.form.zipCode.label')}
          value={postalCode || ''}
        />
        <ReadOnlyField label={t('common.email')} value={email || ''} />
        <ReadOnlyField label={t('common.phone')} value={phoneNumber || ''} />
      </SimpleGrid>
      <CropPledgeSectionActions
        isFormDirty={isDirty}
        sectionStatus={debtorSection!.status}
        onRequestApproval={handleSubmit(onSubmit)}
        isPendingRequestApproval={isPendingRequestApproval}
        onApprove={handleSubmit(onSubmit)}
        isPendingApprove={isPendingSectionApproval}
      />
    </Stack>
  );
};
